<template>
  <div
    :id="`editor-container-${id}`"
    class="stacks-scope"
    ref="editorContainerRef"
  ></div>
</template>

<script>
import { ref, onMounted, nextTick, watch, onBeforeUnmount } from "vue";
import { StacksEditor, RichTextEditor } from "@stackoverflow/stacks-editor";
import "@stackoverflow/stacks";
import "@/assets/scss/stacksEditor.scss";
import "@stackoverflow/stacks-editor/dist/styles.css";
import debounce from "lodash/debounce";

export default {
  props: {
    modelValue: { type: String, default: "" },
    system: { type: String, default: "patch" },
    id: { type: String, default: "stackseditor" },
  },
  emits: ["update:modelValue", "htmlchanged"],

  setup(props, { emit, expose }) {
    // data 정의
    const editorInstance = ref(null);
    const isComposing = ref(false);
    const editorContainerRef = ref(null);
    const mutationObserver = ref(null);
    const _handlers = {
      compositionstart: null,
      compositionend: null,
      blur: null,
    };

    // methods 정의
    const bindDispatch = () => {
      // 트랜잭션 훅으로 모든 변경 감지
      const view = editorInstance.value.editorView;
      const originalDispatch = view.dispatch.bind(view);

      view.dispatch = (tr) => {
        originalDispatch(tr);
        // 트랜잭션 적용 후 content 업데이트
        emit("update:modelValue", editorInstance.value.content);
        debouncedUpdate();
      };
    };
    const initEditor = () => {
      const container = editorContainerRef.value;

      editorInstance.value = new StacksEditor(container, props.modelValue, {
        imageUpload: {
          handler: imageUploadHandler,
        },
      });

      bindDispatch();

      nextTick(() => {
        attachEditableListeners();

        const helpButton = container.querySelector('[data-key="help-link"]');
        if (helpButton) helpButton.style.display = "none";

        if (window.MutationObserver) {
          mutationObserver.value = new MutationObserver(() => {
            attachEditableListeners();
          });
          mutationObserver.value.observe(container, {
            childList: true,
            subtree: true,
            attributes: true,
          });
        }
      });
    };

    const attachEditableListeners = () => {
      const container = editorContainerRef.value;
      if (!container || !editorInstance.value) return;

      const editableElement = container.querySelector("[contenteditable]");
      if (!editableElement) return;

      detachEditableListeners();

      _handlers.compositionstart = () => (isComposing.value = true);

      const updateModelValue = () => {
        nextTick(() => {
          emit("update:modelValue", editorInstance.value.content);
          debouncedUpdate();
        });
      };

      _handlers.compositionend = () => {
        isComposing.value = false;
        updateModelValue();
      };

      _handlers.blur = () => {
        if (!isComposing.value) updateModelValue();
      };

      _handlers.input = () => {
        if (!isComposing.value) updateModelValue();
      };

      editableElement.addEventListener(
        "compositionstart",
        _handlers.compositionstart
      );
      editableElement.addEventListener(
        "compositionend",
        _handlers.compositionend
      );

      editableElement.addEventListener("paste", () => {
        updateModelValue();
      });
      editableElement.addEventListener("blur", _handlers.blur);
      editableElement.addEventListener("input", _handlers.input);
    };

    const detachEditableListeners = () => {
      const container = editorContainerRef.value;
      if (!container) return;

      const editableElement = container.querySelector("[contenteditable]");
      if (!editableElement) return;

      Object.keys(_handlers).forEach((key) => {
        if (_handlers[key]) {
          editableElement.removeEventListener(key, _handlers[key]);
          _handlers[key] = null;
        }
      });
    };
    const imageUploadHandler = async (file) => {
      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(
          `${process.env.VUE_APP_API_BASE_URL}/${props.system}/pr/file/editor-img/add`,
          {
            method: "POST",
            body: formData,
            credentials: "include",
          }
        );

        const data = await response.json();
        const imageUrl = data.url || "";
        if (!imageUrl) throw new Error("이미지 URL이 응답에 없습니다.");

        // 삽입 완료 후 DOM 반영이 끝난 뒤 실행
        nextTick(() => {
          bindDispatch();
        });
        return process.env.VUE_APP_API_BASE_URL + imageUrl;
      } catch (error) {
        console.error(error);
        throw error;
      }
    };
    const stripEditorAttributes = (htmlContent) => {
      // html 문자열에 편집요소 있는 속성들 삭제
      const doc = new DOMParser().parseFromString(htmlContent, "text/html");

      // contenteditable 가진 모든 요소
      doc.querySelectorAll("[contenteditable]").forEach((el) => {
        el.removeAttribute("contenteditable");
      });

      // role="textbox" 제거
      doc.querySelectorAll('[role="textbox"]').forEach((el) => {
        el.removeAttribute("role");
      });

      // aria-multiline 제거
      doc.querySelectorAll("[aria-multiline]").forEach((el) => {
        el.removeAttribute("aria-multiline");
      });

      // translate 제거
      doc.querySelectorAll("[translate]").forEach((el) => {
        el.removeAttribute("translate");
      });
      doc
        .querySelectorAll(".ProseMirror, .js-editor, .outline-none")
        .forEach((el) => {
          el.classList.remove("ProseMirror", "js-editor", "outline-none");
        });
      return doc.body.innerHTML;
    };
    const debouncedUpdate = debounce(() => {
      nextTick(() => {
        const markdown = editorInstance.value.content;

        const tempContainer = document.createElement("div");

        const tempEditor = new RichTextEditor(
          tempContainer,
          markdown,
          editorInstance.value.pluginProvider,
          editorInstance.value.options.richTextOptions
        );

        let htmlContent = tempEditor.editorView.dom.outerHTML;

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, "text/html");

        doc
          .querySelectorAll(".js-language-selector, .js-language-input")
          .forEach((el) => el.remove());

        htmlContent = stripEditorAttributes(doc.body.innerHTML);

        const wrappedHtml = `<div class="stacks-scope">${htmlContent}</div>`;

        tempEditor.destroy();
        emit("htmlchanged", {
          id: props.id,
          html: wrappedHtml,
        });
      });
    }, 200);

    // watch 정의
    watch(
      () => props.modelValue,
      (newVal) => {
        debouncedUpdate();
      }
    );

    expose({
      getContent: () => editorInstance.value?.content || "",
    });

    // life cycle 정의
    onMounted(() => {
      initEditor();
    });

    onBeforeUnmount(() => {
      detachEditableListeners();
      try {
        debouncedUpdate.cancel();
      } catch (error) {
        console.error(error);
      }
      if (mutationObserver.value) {
        try {
          mutationObserver.value.disconnect();
        } catch (error) {
          console.error(error);
        }
      }
    });

    // html에서 사용될 데이터 정의
    return { editorInstance, isComposing, editorContainerRef };
  },
};
</script>

<style lang="scss" scoped>
</style>
